import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get, isEmpty } from "lodash";
import * as moment from "moment-timezone";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import { DataTable, DataTableColumn, GetDataEvent } from "../../../shared/components/data-table/data-table.type";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { FORMAT } from "../../../shared/constants/formats";
import { BoolToStringPipe } from "../../../shared/pipes/bool-to-string/bool-to-string.pipe";
import { UserInfo } from "../../../shared/types/user-info";
import { removePropsWithKeys } from "../../../shared/utils/objects/object";
import { getAssetCategories } from "../assets/asset";
import { CompanyService } from "../companies/company.service";
import { PartnerService } from "../partners/partner.service";
import { getTicketSearchFilterOptions, ticketFailureTypes, ticketPriorities, ticketStatuses } from "./ticket";
import { TicketService } from "./ticket.service";
import { Ticket } from "./ticket.type";

@Injectable({
  providedIn: "root",
})
export class TicketTableService {
  currentUser: UserInfo;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private authenticationService: AuthenticationService,
    private ticketsService: TicketService,
    private companyService: CompanyService,
    private partnerService: PartnerService,
    private dataTableService: DataTableService<Ticket>,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  getTicketsTableConfig = (config?: DataTableConfig): DataTable<Ticket> => {
    const { hidden, shown, actionColumn, entities } = config || {};

    const columns: DataTableColumn<Ticket>[] = (
      [
        {
          prop: "zendeskId",
          name: "ID",
          width: 75,
          link: {
            path: (ticket) => `/tickets/${ticket.id}`,
          },
        },
        {
          prop: "subject",
          name: "Subject",
        },
        {
          prop: "priority",
          name: "Priority",
          render: (ticket) => ticketPriorities.find((priority) => priority.value === ticket.priority)?.label,
        },
        {
          prop: "description",
          name: "Description",
        },
        {
          prop: "status",
          name: "Status",
          render: (ticket) => ticketStatuses.find((status) => status.value === ticket.status)?.label,
        },
        {
          prop: "openedAt",
          name: "Open Date",
          render: (ticket) => moment(ticket.openedAt).tz("America/New_York").format(FORMAT.DATE_TIME),
        },
        {
          prop: "solvedAt",
          name: "Solved At",
          render: (ticket) => {
            if (ticket.solvedAt) {
              return moment(ticket.solvedAt).tz("America/New_York").format(FORMAT.DATE_TIME);
            }
          },
        },
        {
          prop: "requesterEmail", // do not change to 'requester.email' to avoid an issue with csv export
          name: "Requester",
          render: (ticket) => ticket.requester.email,
          exportCsv: { browserColumnKey: "requester.email" },
        },
        {
          prop: "asset.company.name",
          name: "Company",
          hidden: this.currentUser.isCompanyOrViewAsCompany({ ignoreIfMultiCompanies: true }),
          link: {
            path: (ticket) => `/companies/${ticket.asset.companyId}`,
            disabled: () => this.currentUser.isCompanyOrViewAsCompany(),
          },
        },
        {
          prop: "serialNumber",
          name: "Serial #",
        },
        {
          prop: "failureType",
          name: "Failure Type",
          render: (ticket) => ticketFailureTypes.find((type) => type.value === ticket.failureType)?.label,
        },
        {
          prop: "asset.type.model.oem.name",
          name: "Asset OEM",
        },
        {
          prop: "asset.type.model.name",
          name: "Asset Model #",
        },
        {
          prop: "asset.type.name",
          name: "Asset Type",
        },
        {
          prop: "asset.category",
          name: "Asset Category",
          render: (ticket) => getAssetCategories().find((category) => category.value === ticket.asset?.category)?.label,
        },
        {
          prop: "contract.number",
          render: (ticket) => ticket.contract?.numberVersion,
          exportCsv: { browserColumnKey: "contract.numberVersion", csvMapperKey: "contract.numberVersion" },
          name: "Contract #",
          link: {
            path: (ticket) => `/contracts/${ticket.contractId}`,
          },
        },
        {
          prop: "onboarding",
          name: "Onboarding",
          pipes: [{ clazz: BoolToStringPipe }],
        },
      ] as DataTableColumn<Ticket>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (ticket) => col?.inlineEdit?.disabled?.(ticket),
        },
      }));

    return {
      id: "tickets",
      entityName: "Ticket",
      syncWithQueryParams: true,
      toolBar: {
        exportCsv: {
          filename: "tickets",
          data: {
            browser: (queryParams) =>
              this.ticketsService.getTickets({
                ...queryParams,
                where: {
                  ...removePropsWithKeys<Ticket>(queryParams.where, ["company.name"]),
                  assetId: entities?.asset?.id ? entities.asset.id : { $not: null },
                },
                select: {
                  ...queryParams.select,
                  contract: { ...queryParams.select.contract, number: true, version: true },
                },
              }),
            email: (queryParams) =>
              this.ticketsService.getTicketsQueue({
                ...queryParams,
                where: {
                  ...removePropsWithKeys<Ticket>(queryParams.where, ["company.name"]),
                  assetId: entities?.asset?.id ? entities.asset.id : { $not: null },
                },
                select: {
                  ...queryParams.select,
                  contract: { ...queryParams.select.contract, number: true, version: true },
                },
              }),
          },
        },
      },
      selection: {
        hidden: true,
      },
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (ticket) => this.viewTicket(ticket, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.manageTicket,
              hidden: () => actionColumn?.view?.hidden,
            },
          ],
      searchBar: [
        {
          availableSearchFields: getTicketSearchFilterOptions(this.currentUser, {
            hidden,
            shown,
            companyService: this.companyService,
            partnerService: this.partnerService,
          }),
        },
      ],
    };
  };

  getTickets = (event: GetDataEvent<Ticket>, assetId?: string): void => {
    const { onError, onResult, queryParams } = event;
    this.ticketsService
      .getTickets({
        ...queryParams,
        where: {
          // ...removePropsWithKeys(queryParams.where, ["company.name"]),
          ...queryParams.where,
          assetId: assetId ?? { $not: null },
        },
        relations: { contract: true, asset: { company: { partner: true }, type: { model: { oem: true } } } },
        order: isEmpty(queryParams.order) ? { zendeskId: "desc" } : queryParams.order,
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };
  // #region Private methods

  private viewTicket = (ticket: Ticket, config?: DataTableConfig): void => {
    const columns = this.getTicketsTableConfig(config).columns;

    const data = columns
      .filter((col) => !col.hidden)
      .map((col) => ({ name: col.name, value: this.dataTableService.parseData(ticket, col, get(ticket, col.prop)) }));

    this.dialog.open(QuickViewModalComponent, {
      width: "600px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private manageTicket = (ticket: Ticket): void => {
    this.router.navigate([`/tickets/${ticket.id}`]);
  };

  // #endregion
}
